import React, { useEffect, useMemo } from 'react';
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import { Link } from 'react-router-dom';

// Import new architecture components
import { useDashboardContext } from '../../contexts/DashboardContext';
import { LoadingSpinner, DashboardStatsSkeleton } from '../../components/ui/LoadingStates';
import ErrorBoundary, { ErrorAlert, useErrorHandler } from '../../components/ui/ErrorBoundary';
import config from '../../config/config';

// Import CSS
import '../componentCss/webform.css';
import '../componentCss/sidebar.css';
import 'react-toastify/dist/ReactToastify.css';

const ImprovedDashboard = () => {
  const handleError = useErrorHandler();
  
  // Get dashboard context
  const {
    // Data
    statistics,
    userRole,
    daysSinceLastUpdate,
    
    // Loading states
    isLoadingStats,
    
    // Error states
    statsError,
    hasAnyError,
    
    // Actions
    actions
  } = useDashboardContext();

  // Initialize dashboard data on mount
  useEffect(() => {
    const initializeDashboard = async () => {
      try {
        await actions.refreshDashboard();
      } catch (error) {
        handleError(error, 'Dashboard initialization');
      }
    };

    initializeDashboard();
  }, [actions, handleError]);

  // Memoized statistics cards
  const statisticsCards = useMemo(() => [
    {
      title: 'Total Staffs',
      value: statistics.totalStaffs || 0,
      icon: 'users',
      color: '#FFFFFF'
    },
    {
      title: 'Total Patients',
      value: statistics.totalPatients || 0,
      icon: 'user-injured',
      color: '#FFFFFF'
    },
    {
      title: 'Total Forms',
      value: statistics.totalForms || 0,
      icon: 'file-alt',
      color: '#FFFFFF'
    },
    {
      title: 'Total User Groups',
      value: statistics.totalUserGroups || 0,
      icon: 'users-cog',
      color: '#FFFFFF'
    }
  ], [statistics]);

  return (
    <ErrorBoundary component="ImprovedDashboard">
      <div id="content">
        <Container fluid className="p-4">
          {/* Header */}
          <Row>
            <Col>
              <div className="innerhead mb-4">
                <h5 className="headtitle">Dashboard</h5>
              </div>
            </Col>
          </Row>

          {/* Error Display */}
          {hasAnyError && (
            <Row className="mb-4">
              <Col>
                <ErrorAlert
                  error={statsError}
                  onRetry={() => actions.refreshDashboard(true)}
                  onDismiss={() => actions.clearError()}
                />
              </Col>
            </Row>
          )}

          {/* Statistics Cards */}
          <Row className="text-center mb-4">
            {isLoadingStats ? (
              <DashboardStatsSkeleton count={4} />
            ) : (
              statisticsCards.map((card, index) => (
                <Col key={index} md={3} sm={6} className="mb-3">
                  <Card className="statistic-card h-100">
                    <Card.Body className="d-flex align-items-center justify-content-center">
                      <div className="text-center">
                        <div 
                          className="mb-2"
                          style={{ fontSize: '2rem', color: card.color }}
                        >
                          <i className={`fas fa-${card.icon}`}></i>
                        </div>
                        <Card.Title className="h6 mb-2">{card.title}</Card.Title>
                        <Card.Text className="h4 mb-0" style={{ color: card.color }}>
                          {card.value.toLocaleString()}
                        </Card.Text>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))
            )}
          </Row>

          {/* Password Update Notice */}
          {parseInt(daysSinceLastUpdate) >= 83 && (
            <Row className="mb-4">
              <Col>
                <Card className="password-update-message border-warning">
                  <Card.Body className="bg-warning bg-opacity-10">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <strong><i className="fas fa-shield-alt"></i> Security Notice:</strong> It's been {daysSinceLastUpdate || 0} days since you last updated your password. For your security, we recommend updating your password.
                      </div>
                      <Link to={`${config.baseUrl}changePassword`}>
                        <Button variant="warning" size="sm">
                          Update Password
                        </Button>
                      </Link>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Container>
      </div>
    </ErrorBoundary>
  );
};

export default ImprovedDashboard; 